import { useRouter } from 'next/router';
import { Country } from 'react-phone-number-input';
import { freshVenueQueryOptions, useGetVenueByIdQuery } from '../services/s3Api';
import { useGetPartnerVenuesQuery } from '../services/bookingApi';
import { sisterVenueIds } from '../lib/booking/sisterVenues';

export default function useVenueId(): string {
  const router = useRouter();
  const {
    query: { venueId },
  } = router;
  return venueId as string;
}

export function useVenue(locale, slim = true) {
  const venueId = useVenueId();
  const result = useGetVenueByIdQuery({ lang: locale, venueId, slim }, freshVenueQueryOptions);
  return result;
}

/** Triggers a fresh venue fetch on mount. Call once per booking page that lacks a direct query. */
export function useEnsureFreshVenue(locale: string, slim = true) {
  useVenue(locale, slim);
}

export function useOutdoorsActive() {
  const venue = useVenue('en');
  const outdoorsActive = venue?.data?.outdoorsActive;
  return outdoorsActive;
}

export function useBigGroupLimit() {
  const venue = useVenue('en');
  const bigGroupLimit = venue?.data?.bookingBigGroupLimit;
  return bigGroupLimit;
}

export function useBookingTags(){
  const venue = useVenue('en');
  const bookingTags = venue?.data?.bookingTags;
  return bookingTags;
}

/**
 * Ids of the venue and its sister venues, as RelatedVenues resolves them
 * (venuesForPlaceIds). The sisters are the venue's city pool from booking-api;
 * see sisterVenueIds for when the catalog's ?places= is used instead.
 */
export function useRelatedVenues() {
  const venue = useVenue('en');
  const venueId = useVenueId();
  const partners = useGetPartnerVenuesQuery(venueId, { skip: !venueId });
  return sisterVenueIds({
    ownId: venue?.data?.id,
    pool: partners.data?.venueIds,
    poolFailed: partners.isError,
    catalogRelated: venue?.data?.relatedPlaces,
  });
}

export function useVenueCountryCode(): Country {
  const venueId = useVenueId();
  const venueCountryCode = venueId?.split('-')[0]?.toUpperCase();
  return venueCountryCode as Country;
}

export function useVenueOpenHours(locale: string) {
  const venue = useVenue(locale);
  const openHours = venue?.data?.openHours;
  return openHours;
}

export function useVenueTimezone(venueId:string) {
  const venue = useGetVenueByIdQuery({ lang: 'en', venueId, slim: true }, freshVenueQueryOptions);
  const timezone = venue?.data?.timezone; // Replace 'timezone' with the actual field name in your venue data
  return timezone;
}
